import React, {useCallback, useEffect, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';
import PresentationView from '../../components/presentationView/PresentationView';
import {useCaptureRedirectUri} from '../../hooks/useCaptureRedirectUri';
import {useDebouncePresentationState} from '../../hooks/useDebouncePresentationState';
import {useLayout} from '../../hooks/useLayout';
import {usePresentationParams} from '../../hooks/usePresentationParams';
import {useWarnBeforeUnload} from '../../hooks/useWarnBeforeUnload';
import {getPresentationProtection, getPresentationRequestParams, isPresentationOwner} from '../../service/presentation.service';
import {findPresentationByIdAction} from '../../store/actions/dashboard.actions';
import {setRoomInfoModalAction} from '../../store/actions/roomProtection.actions';
import {desktopViewSelector} from '../../store/selectors/layout.selectors';
import {loadedPresentationSelector, presentationStatusSelector} from '../../store/selectors/presentation.selectors';
import {loggedInUserSelector} from '../../store/selectors/auth.selector';
import BaseLayout from '../layout/BaseLayout';
import {ProtectionInfo, ProtectionType} from '../../incase-domain/presentationProtection';
import IdentificationModal from '../../components/presentationView/parts/components/IdentificationModal';
import UserAuthModal from '../../components/presentationView/parts/components/UserAuthModal';
import {hasCommonElements} from '../../util/array';
import {GuestCredentials} from '../../components/presentationView/parts/components/GuestModal';

function PresentationPage(): JSX.Element {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const navigate = useNavigate();
    useCaptureRedirectUri();
    const {presentationId, params} = usePresentationParams();
    const loadedPresentation = useSelector(loadedPresentationSelector);
    const presentationStatus = useSelector(presentationStatusSelector);
    const isDesktopViewSelected = useSelector(desktopViewSelector);
    const loggedInUser = useSelector(loggedInUserSelector);
    const [protection, setProtection] = useState<ProtectionInfo>();
    const [credential, setCredential] = useState<GuestCredentials>();
    const debouncePresentationState = useDebouncePresentationState();
    const {mobileView} = useLayout();
    useWarnBeforeUnload(!!loadedPresentation);

    useEffect(() => {
        (async () => {
            if (!presentationId) {
                navigate('/');
                return;
            }
            if (loadedPresentation?._id === presentationId) return;

            const [protection, requestParams] = await Promise.all([
                getPresentationProtection(presentationId),
                getPresentationRequestParams(presentationId),
            ]);
            const missingParams = (requestParams ?? [])
                .filter((param) => param.required && !params[param.key]?.trim())
                .map((param) => param.key);

            if (missingParams.length > 0) {
                dispatch(
                    setRoomInfoModalAction(true, 'lobby.modals.missingRequestParam', 'lobby.modals.missingRequestParamsDesc', {
                        params: missingParams.join(', '),
                    })
                );
                return;
            }

            const isOwner = await isPresentationOwner(presentationId);
            if (isOwner) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
                return;
            }

            if (!protection || protection.types.length === 0) {
                dispatch(setRoomInfoModalAction(true, 'lobby.modals.presPrivate', 'lobby.modals.presPrivateDesc'));
                return;
            }

            setProtection(protection);

            if (protection.types.includes(ProtectionType.GUEST) && protection.types.length === 1) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
            }

            const externalAuthProtections = [ProtectionType.EMEDIKUS, ProtectionType.AAI_EDU, ProtectionType.ZOOM];
            if (loggedInUser && hasCommonElements(protection.types, externalAuthProtections)) {
                dispatch(findPresentationByIdAction(presentationId, undefined, params));
            }
        })();
    }, [dispatch, presentationId, params, loadedPresentation?._id, navigate, loggedInUser]);

    const findPresentation = useCallback(
        (cr?: GuestCredentials): void => {
            setCredential(cr);
            if (presentationId) dispatch(findPresentationByIdAction(presentationId, cr || credential, params));
        },
        [credential, dispatch, params, presentationId]
    );

    const isPresFound = !presentationStatus.found && presentationStatus.id === presentationId;
    if (isPresFound || !presentationId) {
        navigate('/');
    }

    return (
        <BaseLayout hasPresActions>
            <>
                <div className={`p-player ${isDesktopViewSelected ? 'p-player--desktop' : ''}`}>
                    <ErrorBoundary
                        resetKeys={[mobileView]}
                        FallbackComponent={() => {
                            toast.warning(t('playerPage.warning'), {toastId: 'player-warning'});
                            return null;
                        }}
                        onError={() => debouncePresentationState()}
                    >
                        <PresentationView />
                    </ErrorBoundary>
                </div>

                <UserAuthModal
                    protection={
                        protection
                            ? {
                                  protectionTypes: protection.types,
                                  identificationLabel: protection.identificationLabel,
                              }
                            : undefined
                    }
                    findPresentation={findPresentation}
                />

                <IdentificationModal />
            </>
        </BaseLayout>
    );
}

export default PresentationPage;
